import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ContentModal from "components/ContentModal";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ConfirmationModal from "./ConfirmationModal";
import Api from "api";
import { postUserActivity } from "components/shared/postUserActivity";
import { WORKFLOW_SAVE_ADOPTION_DECISION } from "constants/Constants";

const AdoptionDecisionsModal = ({
  quarter,
  year,
  filteredPolicies,
  adoptionOptions,
  allLineOfBusinessDetailsList,
  handleClose,
  actionType,
  isBreakFix,
  onSuccess,
}) => {
  const [currentPolicyIndex, setCurrentPolicyIndex] = useState(0);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [rowsData, setRowsData] = useState([]); // Holds the LOB data for the current policy
  const [rowUpdated, setRowUpdated] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // Track editing mode
  const [completedPolicies, setCompletedPolicies] = useState([]); // Holds LOB data for all completed policies

  // Sticky state for Adoption Decision and Effective Date
  const [stickyDecisionData, setStickyDecisionData] = useState({});
  const [stickyDateData, setStickyDateData] = useState({});
  const [errorCells, setErrorCells] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);

  const currentPolicy = filteredPolicies[currentPolicyIndex];

  const getLineOfBusinessDetails = async (healthPlanKey, policyKey) => {
    try {
      const response = await Api.policyEnforcementGrid.getPolicyLobConfig(
        healthPlanKey,
        policyKey
      );
      return response.data; // Assuming the API returns the LOB details list
    } catch (error) {
      console.error("Failed to fetch LOB details:", error);
      setDialog({
        message: error.response?.data?.errors,
        title: error.code + " :: " + error.message,
      });
      return [];
    }
  };

  const getSavedAsDraftDetails = async (healthPlanKey, policyKey) => {
    try {
      if (!isBreakFix) {
        return []; // No drafts if not coming from hp config screen
      }
      const response = await Api.healthPlanConfig.getDraftAdoptionDecisions(
        healthPlanKey,
        policyKey
      );
      const data = response.data;
      if (!data || data.length === 0) {
        console.warn("No draft adoption decision available.");
        return [];
      }
      return data;
    } catch (error) {
      console.error("Error fetching draft adoption decisions", error);
      return [];
    }
  };

  const memoizedCompletedPolicies = useMemo(
    () => completedPolicies,
    [completedPolicies]
  );
  const memoizedStickyDateData = useMemo(
    () => stickyDateData,
    [stickyDateData]
  );
  const memoizedStickyDecisionData = useMemo(
    () => stickyDecisionData,
    [stickyDecisionData]
  );

  // Load LOB data when policy changes
  useEffect(() => {
    // Define an async function to fetch the LOB data
    const loadLobData = async () => {
      try {
        const lobData = await getPolicyLobData(); // Await the LOB data
        setRowsData(lobData); // Set the state with the loaded data
      } catch (error) {
        console.error("Failed to load LOB data:", error);
        // Optionally, you can set an empty array or an error message in the state
        setRowsData([]);
      } finally {
        setLoading(false);
      }
    };

    // Function to get LOB data for the current policy
    const getPolicyLobData = async () => {
      if (!currentPolicy) {
        return;
      }
      const policyInCompleted = memoizedCompletedPolicies.find(
        (policy) => policy.policyKey === currentPolicy.policyKey
      );

      if (policyInCompleted) {
        // If the policy is already in memoizedCompletedPolicies, return its saved LOB data
        return policyInCompleted.lineOfBusinessDetailsList;
      } else {
        const { healthPlanKey, policyKey } = currentPolicy;
        try {
          // 1) fetch draft-first, then policy LOBs
          const draftAdoptionDecisions = await getSavedAsDraftDetails(
            healthPlanKey,
            policyKey
          );
          const policyLineOfBusinessDetailsList =
            await getLineOfBusinessDetails(healthPlanKey, policyKey);

          // 2) choose the source: drafts take precedence
          const chosenLobList =
            Array.isArray(draftAdoptionDecisions) &&
            draftAdoptionDecisions.length > 0
              ? draftAdoptionDecisions
              : policyLineOfBusinessDetailsList;

          // 3) merge with allLineOfBusinessDetailsList to ensure all LOBs are present
          //    and apply sticky decisions/dates where applicable
          return allLineOfBusinessDetailsList.map((lob) => {
            const matchedLob = chosenLobList.find((plob) => plob.id === lob.id);

            const updatedLob = {
              id: lob.id,
              lobDesc: lob.lobDesc,
              adoptionDecision:
                matchedLob?.adoptionDecision ||
                memoizedStickyDecisionData[lob.id] || // Apply sticky decision if available
                "",
              stickyDecision:
                matchedLob?.stickyDecision || memoizedStickyDecisionData[lob.id]
                  ? true
                  : false,
              effectiveDate:
                matchedLob?.effectiveDate ||
                memoizedStickyDateData[lob.id] || // Apply sticky date if available
                dayjs(new Date()).format("YYYY-MM-DD"), // Default to today's date if not set
              stickyDate:
                matchedLob?.stickyDate || memoizedStickyDateData[lob.id]
                  ? true
                  : false,
              endDate: matchedLob?.endDate || null,
              varianceComments: matchedLob?.varianceComments || "",
            };

            const isAdoptionDecisionFromMemoizedData =
              !matchedLob?.adoptionDecision &&
              memoizedStickyDecisionData[lob.id];
            const isEffectiveDateFromMemoizedData =
              !matchedLob?.effectiveDate && memoizedStickyDateData[lob.id];

            if (
              isAdoptionDecisionFromMemoizedData ||
              isEffectiveDateFromMemoizedData ||
              (matchedLob && isBreakFix)
            ) {
              processRowUpdate(updatedLob);
            }

            return updatedLob;
          });
        } catch (error) {
          console.error("Failed to fetch policy LOB data:", error);
          // Handle the error in the UI, e.g., show a message to the user
          return []; // Returning an empty array to avoid further errors in the UI
        }
      }
    };

    loadLobData();
  }, [currentPolicy]);

  // Save the current policy's LOB data into completedPolicies
  const saveCurrentPolicyData = () => {
    if (rowUpdated) {
      const updatedPolicies = [...completedPolicies];
      const existingPolicyIndex = updatedPolicies.findIndex(
        (policy) => policy.policyKey === currentPolicy.policyKey
      );

      const updatedPolicy = {
        ...currentPolicy,
        lineOfBusinessDetailsList: rowsData,
        year: year,
        quarter: quarter,
        ...(actionType && { actionType }),
      };

      if (existingPolicyIndex !== -1) {
        updatedPolicies[existingPolicyIndex] = updatedPolicy;
      } else {
        updatedPolicies.push(updatedPolicy);
      }

      setCompletedPolicies(updatedPolicies);
    }
  };

  const handleProcessRowUpdateError = (error) => {
    // You can log the error, show a message, or handle it as needed
    console.error("Row update failed:", error);
    alert(error.message); // Display an alert, or use a Snackbar for better UX
  };

  const hasRowErrors = () => {
    return Object.keys(errorCells).length > 0;
  };

  // Handle row edit completion to save data
  const processRowUpdate = (newRow, oldRow) => {
    const { id, adoptionDecision, effectiveDate, varianceComments } = newRow;

    // Reset error state for the current row
    setErrorCells((prevErrors) => {
      const errors = { ...prevErrors };

      // Validate AdoptionDecision
      if (!adoptionDecision) {
        errors[id] = { ...errors[id], adoptionDecision: true };
      } else if (errors[id]?.adoptionDecision) {
        delete errors[id].adoptionDecision;
      }

      // Validate EffectiveDate
      if (!effectiveDate) {
        errors[id] = { ...errors[id], effectiveDate: true };
      } else if (errors[id]?.effectiveDate) {
        delete errors[id].effectiveDate;
      }

      const matchedOption = adoptionOptions.find(
        (o) => o.statusCode === adoptionDecision
      );

      // Get both code and description as lowercase strings
      const codeText = adoptionDecision?.toString().toLowerCase() ?? "";
      const descText = matchedOption?.statusDescription?.toLowerCase() ?? "";

      // Check if "variance" is in either one
      const requiresVarianceComments =
        codeText.includes("variance") || descText.includes("variance");

      if (requiresVarianceComments && !varianceComments) {
        errors[id] = { ...errors[id], varianceComments: true };
      } else if (errors[id]?.varianceComments) {
        delete errors[id].varianceComments;
      }

      // Clean up empty error entries
      if (errors[id] && Object.keys(errors[id]).length === 0) {
        delete errors[id];
      }

      return errors;
    });

    const updatedRows = rowsData.map((row) =>
      row.id === newRow.id ? newRow : row
    );
    setRowUpdated(true);
    setRowsData(updatedRows);

    // Check for sticky decisions and dates, and update sticky state if checked
    if (newRow.stickyDecision) {
      setStickyDecisionData((prevState) => ({
        ...prevState,
        [newRow.id]: newRow.adoptionDecision, // Remember the adoption decision
      }));
    }

    if (newRow.stickyDate) {
      setStickyDateData((prevState) => ({
        ...prevState,
        [newRow.id]: newRow.effectiveDate, // Remember the effective date
      }));
    }

    return newRow;
  };

  const handleRowEditStart = (params) => {
    setIsEditing(true);
  };

  const handleRowEditStop = () => {
    setIsEditing(false);
  };

  const handleNext = () => {
    saveCurrentPolicyData(); // Save the current policy data before moving to the next policy
    if (currentPolicyIndex < filteredPolicies.length - 1) {
      setCurrentPolicyIndex(currentPolicyIndex + 1);
    }
    setRowUpdated(false);
    setErrorCells({});
  };

  const handleBack = () => {
    saveCurrentPolicyData(); // Save the current policy data before moving to the previous policy
    if (currentPolicyIndex > 0) {
      setCurrentPolicyIndex(currentPolicyIndex - 1);
    }
    setRowUpdated(false);
    setErrorCells({});
  };

  const handleGoToConfirmation = () => {
    saveCurrentPolicyData(); // Ensure the current policy's data is saved
    setRowUpdated(false);
    setErrorCells({});
    setConfirmationModalOpen(true); // Open confirmation modal
  };

  const handleSaveAsDraft = async () => {
    setLoading(true);
    try {
      const response =
        await Api.healthPlanConfig.saveAdoptionDecisionsAsDraft(
          completedPolicies
        );
      if (response.status === 200) {
        onSuccess?.({
          message: "Adoption Decisions Saved as Draft Successfully.",
        });
      } else {
        setDialog({
          message:
            "Problem saving Adoption Decisions as Draft" + response?.message,
          title: "Error",
        });
      }
    } catch (error) {
      setDialog({
        message: error.response?.data?.errors,
        title: error.code + ":: " + error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const response = await Api.policyEnforcementGrid.saveAdoptionDecisions(
        completedPolicies,
        isBreakFix
      );
      if (response.status === 200) {
        // Post user activity after successful save
        await postUserActivity(
          currentPolicy.healthPlanKey,
          currentPolicy.policyKey,
          response.status,
          WORKFLOW_SAVE_ADOPTION_DECISION,
          "Click",
          "AdoptionDecisionsModal",
          "Save",
          "Adoption Decisions"
        );
        setConfirmationModalOpen(false);
        // gather which policies were saved (confirmation screen can include multiple)
        const savedPolicyKeys = completedPolicies.map((p) => p.policyKey);
        onSuccess?.({
          message: "Adoption Decisions Saved Successfully.",
          savedPolicyKeys,
        });
      } else {
        setDialog({
          message: "Problem saving Adoption Decisions" + response?.message,
          title: "Error",
        });
      }
    } catch (error) {
      setDialog({
        message: error.response?.data?.errors,
        title: error.code + ":: " + error.message,
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDialogClose = () => {
    setDialog(null);
  };
  const codeToLabel = (code) =>
    adoptionOptions.find((o) => o.statusCode === code)?.statusDescription ??
    code;

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            <CircularProgress />
          </Box>
        )}
        {dialog && (
          <Dialog open onClose={handleDialogClose}>
            <DialogTitle>{dialog.title}</DialogTitle>
            <DialogContent>{dialog.message}</DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose}>Close</Button>
            </DialogActions>
          </Dialog>
        )}
        <ContentModal
          open={true}
          handleClose={handleClose}
          maxWidth={"lg"}
          title={
            <Box
              sx={{
                display: "flex",
                fontWeight: "bold",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <span>{`Enter Adoption Decisions: ${quarter} - ${year}`}</span>
              <Button
                onClick={handleGoToConfirmation}
                variant="contained"
                disabled={isEditing || hasRowErrors()}
              >
                GO TO CONFIRMATION
              </Button>
            </Box>
          }
          content={
            <Box>
              <Typography sx={{ fontWeight: "bold" }} gutterBottom>
                Policy: {currentPolicy?.policyNumber} -{" "}
                {currentPolicy?.policyTitle}
              </Typography>
              <DataGrid
                pageSize={allLineOfBusinessDetailsList.length}
                processRowUpdate={processRowUpdate} // Called when a row is edited
                onProcessRowUpdateError={handleProcessRowUpdateError}
                editMode="row" // Use row editing mode
                onRowEditStart={handleRowEditStart} // Start editing
                onRowEditStop={handleRowEditStop} // Stop editing
                sx={{
                  ".MuiDataGrid-columnHeaderTitle": {
                    fontWeight: "bold", // This makes the header text bold
                  },
                  "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer":
                    {
                      visibility: "hidden", // Hide column menu buttons
                      width: 0,
                    },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    width: "100%", // Ensure headers take full width
                  },
                  "& .MuiDataGrid-row": {
                    border: "none", // Keep row-level borders
                  },
                  "& .MuiDataGrid-cell": {
                    border: "none", // Remove borders between individual cells
                  },
                  "& .errorCell": {
                    border: "1px solid red !important",
                    color: "red",
                  },
                }}
                getCellClassName={(params) => {
                  // Apply 'errorCell' class if the cell is in errorCells
                  if (errorCells[params.id]?.[params.field]) {
                    return "errorCell";
                  }
                  return "";
                }}
                disableSelectionOnClick
                disableColumnMenu
                hideFooter
                rows={rowsData} // Use the merged LOB list directly as rows
                columns={[
                  {
                    field: "lobDesc",
                    headerName: "Line of Business",
                    flex: 1,
                    editable: false,
                  },
                  {
                    field: "adoptionDecision",
                    headerName: "Adoption Decision",
                    flex: 1,
                    editable: true,
                    type: "singleSelect",
                    valueOptions: adoptionOptions.map((option) => ({
                      value: option.statusCode,
                      label: option.statusDescription,
                      key: option.statusKey, // adding a unique key for each option
                    })),
                    renderCell: (params) => {
                      if (
                        params.row.id in errorCells &&
                        params.field in errorCells[params.row.id]
                      ) {
                        return "Decision required";
                      }
                      if (params.value) {
                        return codeToLabel(params.value);
                      }
                      return "";
                    },
                  },
                  {
                    field: "stickyDecision",
                    headerName: "Sticky Decision?",
                    flex: 0.5,
                    type: "boolean",
                    editable: true,
                  },
                  {
                    field: "effectiveDate",
                    headerName: "Effective Date",
                    flex: 1,
                    editable: true,
                    valueFormatter: (value) => {
                      if (value) {
                        return dayjs(value).format("MM/DD/YYYY");
                      }
                      return dayjs(new Date()).format("MM/DD/YYYY");
                    },
                    renderEditCell: (params) => {
                      const value = params.value
                        ? dayjs(params.value)
                        : dayjs(new Date());
                      return (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DesktopDatePicker
                            value={value}
                            format="MM/DD/YYYY"
                            onChange={(date) => {
                              const next = date
                                ? dayjs(date).format("YYYY-MM-DD")
                                : dayjs().format("YYYY-MM-DD");
                              params.api.setEditCellValue({
                                id: params.id,
                                field: "effectiveDate",
                                value: next,
                              });
                            }}
                          />
                        </LocalizationProvider>
                      );
                    },
                  },
                  {
                    field: "stickyDate",
                    headerName: "Sticky Date?",
                    flex: 0.5,
                    type: "boolean",
                    editable: true,
                  },
                  {
                    field: "endDate",
                    headerName: "End Date",
                    flex: 1,
                    editable: true,
                    valueFormatter: (value) => {
                      if (value) {
                        return dayjs(value).format("MM/DD/YYYY");
                      }
                      return "";
                    },

                    renderEditCell: (params) => {
                      const value = params.value ? dayjs(params.value) : null;
                      return (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DesktopDatePicker
                            value={value}
                            format="MM/DD/YYYY"
                            onChange={(date) =>
                              params.api.setEditCellValue({
                                id: params.id,
                                field: "endDate",
                                value: date
                                  ? dayjs(date).format("YYYY-MM-DD")
                                  : "",
                              })
                            }
                          />
                        </LocalizationProvider>
                      );
                    },
                  },
                  {
                    field: "varianceComments",
                    headerName: "Variance Comments",
                    flex: 2,
                    editable: true,
                    renderCell: (params) => {
                      if (
                        params.row.id in errorCells &&
                        params.field in errorCells[params.row.id]
                      ) {
                        return "Variance Comment required";
                      }
                      return params.value;
                    },
                  },
                ]}
              />
            </Box>
          }
          actions={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Button
                onClick={handleBack}
                disabled={
                  isEditing || currentPolicyIndex === 0 || hasRowErrors()
                }
              >
                BACK
              </Button>
              <Button
                onClick={handleNext}
                disabled={
                  isEditing ||
                  currentPolicyIndex === filteredPolicies.length - 1 ||
                  hasRowErrors()
                }
              >
                NEXT
              </Button>
            </Box>
          }
        />
      </LocalizationProvider>

      {confirmationModalOpen && (
        <ConfirmationModal
          policiesData={completedPolicies} // Pass all completed policies to confirmation modal
          adoptionOptions={adoptionOptions}
          handleGoBack={() => setConfirmationModalOpen(false)}
          handleClose={handleClose}
          handleSave={handleSave}
          handleSaveAsDraft={handleSaveAsDraft}
          showDraftButton={isBreakFix}
        />
      )}
    </Box>
  );
};

export default AdoptionDecisionsModal;
